import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

/**
 * PublishStatusCard — shared, prominent "Published / Unpublished" toggle used
 * across every CMS admin panel so admins get a consistent publish story.
 *
 * Green card + Eye icon when published, amber card + EyeOff when unpublished.
 * Clear subtitle explains exactly what unpublishing does per entity type.
 *
 * Usage:
 *   <PublishStatusCard
 *     isPublished={formData.is_active}
 *     onChange={(v) => handleInputChange('is_active', v)}
 *     isDark={isDark}
 *     entityLabel="location"
 *     publicRoute="/locations"
 *   />
 */
const PublishStatusCard = ({
  isPublished,
  onChange,
  isDark = false,
  entityLabel = 'page',
  publicRoute,
  testId = 'publish-status-toggle',
}) => {
  const activeClass = isDark
    ? 'bg-emerald-500/5 border-emerald-500/30'
    : 'bg-emerald-50 border-emerald-200';
  const inactiveClass = isDark
    ? 'bg-amber-500/5 border-amber-500/30'
    : 'bg-amber-50 border-amber-200';

  return (
    <div className={`p-4 rounded-lg border ${isPublished ? activeClass : inactiveClass}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {isPublished ? (
            <Eye className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
          ) : (
            <EyeOff className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
          )}
          <div>
            <Label className={`text-base font-semibold ${isDark ? 'text-white' : ''}`}>
              {isPublished
                ? `Published — visible on public site`
                : `Unpublished — hidden from public`}
            </Label>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
              {isPublished ? (
                <>This {entityLabel} is live on the public site.</>
              ) : (
                <>
                  When off, this {entityLabel} is hidden from
                  {publicRoute ? (
                    <>
                      {' '}
                      <code>{publicRoute}</code> and its detail page returns 404
                    </>
                  ) : (
                    <> the public site</>
                  )}
                  .
                </>
              )}
            </p>
          </div>
        </div>
        <Switch
          checked={!!isPublished}
          onCheckedChange={onChange}
          data-testid={testId}
        />
      </div>
    </div>
  );
};

export default PublishStatusCard;
